<template>
  <v-card flat>
    <h1>{{ $t("application.profession.orientation.title") }}</h1>
    <!-- <p>{{ getProfessionNameById(selectedApplication.professionId) }}</p> -->
    <p>{{ $t("application.profession.orientation.text") }}</p>
    <p class="text-caption">
      <b>{{ $t("application.profession.orientation.sub-text") }}</b>
    </p>

    <p
      class="text-caption"
      v-if="orientations.some(({ name }) => acquiredOrientations.includes(name))
    ">
      {{ $t("application.profession.orientation.acquired") }}
      <b>{{ acquiredOrientations }}</b>
    </p>

    <!-- Form with profession orientation name input field -->
    <v-form>
      <v-text-field
        v-model="search"
        class="search-field mb-5"
        prepend-icon="$sl-search"
        single-line
        hide-details
        :label="$t('application.search-label')"
      />
    </v-form>

    <!-- Display professions as a list including machine professions if any -->
    <v-card flat>
      <v-list class="height-small">
        <v-list-item-group
          v-model="selectedOrientationIds"
          :multiple="true"
          
        >
          <template v-for="(item, index) in orientations">
            <v-list-item
              :key="`item-${index}`"
              :value="item.id"
              :disabled="acquiredOrientations.includes(item.name)"
            >
              <v-list-item-icon>
                <v-icon>$sl-{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content class="ml-4">
                <v-list-item-title v-html="item.name"></v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon>
                <v-checkbox
                  v-model="selectedOrientationIds"
                  :value="item.id"
                  @click.native="check($event)"
                ></v-checkbox>
              </v-list-item-icon>
            </v-list-item>
            <v-divider :key="`divider-${index}`"></v-divider>
          </template>
        </v-list-item-group>
      </v-list>
    </v-card>

    <!-- Enable button if any profession orientation is selected and display different button text based on is company user or apprentice -->
    <div v-if="showDocumectsFeature">
      <NextButton
        :isDisabled="!selectedOrientationIds || selectedOrientationIds.length === 0 || !exludedExistingOrientations.length"
        @click="nextStep"
      >
        {{
          selectedApplication && selectedApplication.isCompanyApplication ? $t("labels.instructor-workplaces-next-step") : $t("tour.next")
        }}
      </NextButton>
    </div>
    <div v-else>
      <NextButton
        :isDisabled="!selectedOrientationIds || selectedOrientationIds.length === 0 || !exludedExistingOrientations.length"
        @click="nextStep"
      >
        {{
          selectedApplication && selectedApplication.isCompanyApplication
            ? $t("labels.instructor-workplaces-next-step")
            : $t("btn.company-next-step")
        }}
      </NextButton>
    </div>

    <!-- Go to a previous  step-->
    <BottomNav @clicked="$emit('goToStep', '-1')" />
  </v-card>
</template>

<script>
import API from "@/services/api.js";
import { mapActions, mapGetters } from "vuex";
import get from "lodash/get";
import filter from "lodash/filter";
import without from "lodash/without";
import concat from "lodash/concat";
import sortBy from "lodash/sortBy";
import isEmpty from "lodash/isEmpty";
import BottomNav from "@/components/Application/BottomNav";
import NextButton from "@/components/Common/NextButton";
import ActionButton from '@/components/Common/ActionButton.vue'
import { uniq } from "lodash";

const SugarDate = require('sugar/date')

// @group Applications/Common
// ApplicationCompanies component
export default {
  name: "ApplicationProfessionOrientation",

  components: {
    BottomNav,
    NextButton,
    ActionButton
  },
  props: {
    showDocumectsFeature: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    selectedOrientationIds: [],
    search: "",
    transferOldProfession: false
  }),

  created() {
    this.initApplication();
  },
  /**
   * @vuese
   * Used to watch the selectedApplication and currentStep,  call initApplication
   * @arg called with no argument
   */
  watch: {
    "selectedApplication.professionId": {
      handler: function () {
        this.initApplication();
      },
    },
    currentStep: {
      handler: function () {
        this.initApplication();
      },
    },
  },

  computed: {
    ...mapGetters("currentuser", ["currentUser", "isApprentice", "isCompanyUser"]),
    ...mapGetters("application", ["currentStep", "selectedApplication"]),
    ...mapGetters("basic", [
      "basicProfessions",
      "hasProfessionOrientations",
      "getProfessionNameById",
      "getProfessionById",
    ]),

    /**
     * @vuese
     * Used get all orientations if that specific profession is a machine profession
     */
    orientations() {
      if (!(get(this,'selectedApplication.professionId',false))) return [];

      let prof = this.getProfessionById(this.selectedApplication.professionId)
      let orient = (prof ? prof.machineOrientations : [])
       
      if (this.search != "") {
        let normalizedSearch = this.search.toLowerCase();
        orient = orient.filter((x) => x.name && x.name.toLowerCase().includes(normalizedSearch));
      }

      //- Deafult included first
      return sortBy(orient, function (o) {return o.isDefaultIncluded ? 0 : 1;})

    },

    // This function checks if an apprentice already has any machines regardless of being in GU or KTID.
    // If there is a match, then initialize a transfer to the new company.
    professionOrOrientationsBelongsToEducation() {
      const selectedOrientations = get(this, 'selectedOrientationIds', [])
      const sameProfession = get(this, 'currentUser.educations', []).find(({ professionId }) => get(this, 'profession.id', '') === professionId)

      return get(this, 'currentUser.educations', [])
          .some(({ educationProfessionOrientations }) => educationProfessionOrientations
          .find(({ professionMachineOrientationId }) => selectedOrientations.includes(professionMachineOrientationId))) || sameProfession
    },

    profession() {
      return this.getProfessionById(get(this, 'selectedApplication.professionId', null))
    },

    acquiredOrientations() {
      return get(this, 'orientations', [])
        .filter(({ id }) => this.acquiredOrientationIds.includes(id))
        .map(({ name }) => name)
        .sort()
        .join(', ')
    },

    acquiredOrientationIds() {
      return uniq(get(this, 'currentUser.educations', [])
        .map(({ educationProfessionOrientations }) => 
          educationProfessionOrientations.map(({ professionMachineOrientationId }) => 
          professionMachineOrientationId))
          .flat())
    },

    exludedExistingOrientations() {
      return this.selectedOrientationIds
      .filter(id => !this.acquiredOrientationIds.includes(id))
      .map(orientationId => ({
        applicationId: this.selectedApplication.id,
        professionMachineOrientationId: orientationId,
      }))
    }
  },

  methods: {
    get,

    ...mapActions("application", ["updateSelectedApplication"]),
    /**
     * @vuese
     * Used to Initialize application with either selected Orientations or apply the default Orientation
     */
    /*
        Om lärling redan har YB i ett maskinyrke ska inte denna maskin vara valbar, inaktivera valet.
        Om lärling redan är i K-tid för ett maskinyrke ska inte denna maskin vara valbar, inaktivera.
        Om lärlingen inte är registrerad eller systemet inte kan hitta YB eller k-tid ska denna maskin vara valbar
      */
    alreadyHasThisMachine(machineId){
      if(get(this, 'selectedApplication.personalNumber', 'x') !== get(this, 'currentUser.personalNumber', 'y')) {
        return false
      }

      //- Grab all user TCs with (potentially) this machine
      const userMachineTCs = get(this, 'currentUser.tradeCertificates', []).filter(tc => this.hasProfessionOrientations(tc.professionId))
      const userHasTCsWithMachine = userMachineTCs.filter( tc => {
        const prof = this.basicProfessions.find( p  => p.id == tc.professionId)
        if (prof) {
          return prof.machineOrientations.find( mo => mo.machineId == machineId)
        }
        return false
      })
      //- Grab all users MQs for this machine
      const userHasMQForMachine =  get(this, 'currentUser.machinequalifications', []).find(mq => mq.machineId == machineId)

      //- Check for TradeCertificate in machine Profession orientation AND machine in MachineQualification
      if (!isEmpty(userHasTCsWithMachine) && userHasMQForMachine) {
        //-   Nope, don't display
        console.log('User has TC in profession with this machine and MQ for it already')
        return true
      }

      //- Check if user has an education with this machine in k-tid
      const userHasMachineEducationInKTid = get(this, 'currentUser.educations', []).filter( ed => {
        const isInKtid = ed.qualificationTime &&  SugarDate.Date.isBetween(new Date(), ed.qualificationTime.fromDate, ed.qualificationTime.toDate)
        const edHasMachine = ed.educationProfessionOrientations.find(epo => get(epo, 'professionMachineOrientation.machineId', null) == machineId)
        if (isInKtid && edHasMachine) {
          return true
        }
      });

      if (! isEmpty(userHasMachineEducationInKTid)) {
        //-   Nope, don't display
        console.log('User is in K-tid for Education with this machine in professionMachineOrientation')
        return true
      }

      //- Yepp, display
      return false
    },

    initApplication() {
       const defaultOrientationIds = this.orientations.filter(o => {
         return (o.isDefaultIncluded==true && this.alreadyHasThisMachine(o.machineId)==false)
       }).map((m) => m.id)

      if ( get(this, 'selectedApplication.professionOrientations.length', 0) ) {
        this.selectedOrientationIds = get(this, 'orientations', []).filter(({ id }) => this.acquiredOrientationIds.includes(id)).map(({ id }) => id)
        || this.selectedApplication.professionOrientations.map((o) => o.professionMachineOrientationId)
      } else {
        this.selectedOrientationIds = get(this, 'orientations', []).filter(({ id }) => this.acquiredOrientationIds.includes(id)).map(({ id }) => id) || defaultOrientationIds
      }
    },
    /**
     * @vuese
     * Used to go to the next step if form is valid and update the application with new criterias
     * @arg The argument is an integer value application  id and params are the orientations selected
     */
    nextStep() {
      let data

      if (this.selectedOrientationIds.length > 0) {
        const selIds = this.selectedOrientationIds;
        const oldOnes = filter(
          this.selectedApplication.professionOrientations,
          function (o) {
            return selIds.includes(o.professionMachineOrientationId);
          }
        );
        const newIds = without(
          this.selectedOrientationIds,
          ...oldOnes.map((o) => o.professionMachineOrientationId)
        );
        const newOnes = filter(this.orientations, function (o) {
          return newIds.includes(o.id);
        }).map((o) => {
          return {
            applicationId: this.selectedApplication.id,
            professionMachineOrientationId: o.id,
          };
        });

        const selectedOrientations = concat(oldOnes, newOnes);

        data = {
          ...this.selectedApplication,
          saveProfessionOrientations: this.exludedExistingOrientations,
          transferOldProfession: !!this.professionOrOrientationsBelongsToEducation && !!this.transferOldProfession
        }

        API.updateApplication(this.selectedApplication.id, { data })
          .then(() => {
            //- We might have new ID's
            API.getApplication(this.selectedApplication.id)
              .then((res) => {
                /**
                 * @vuese
                 * Used to update application in vuex store with profession Orientations selected
                 */
                this.updateSelectedApplication({
                  ...this.selectedApplication,
                  professionOrientations: res.data.professionOrientations,
                  transferOldProfession: get(res, 'data.transferOldProfession', false)
                });
                
                // Fire 1
                // @arg The argument is a integer representing (in which step to go)
                let step = 7
                if ( get(this, 'selectedApplication.isCompanyApplication', null) )
                  step = 9

                this.$emit("goToStep", `${ this.professionOrOrientationsBelongsToEducation && !!this.transferOldProfession ? step : '+1'}`);
              })
              .catch((error) => {
                this.$events.fire("showError", this.$t("errors.form-validation"));
                console.log("There was an error:", error);
              });
          })
          .catch((error) => {
            this.$events.fire("showError", this.$t("errors.form-validation"));
            console.log("There was an error:", error);
          });
      }
    },
    /**
     * @vuese
     * Used to andle prevents propagation of the event
     */
    check: function (e) {
      e.cancelBubble = true;
    },

    readInformation() {
      this.transferOldProfession = true
      this.nextStep()
    },
  },
};
</script>

<style scoped lang="scss"></style>
